/**
 * `GET/POST venue/v1/manager/venues/{venueId}/closures` ·
 * `DELETE venue/v1/manager/venues/{venueId}/closures/{closureId}` 계약 사본(#604).
 *
 * 계약 출처: 허브에 `api-spec.md` 가 아직 게시되지 않아, 이미 병합된 `facecock-was` 소스
 * (`VenueManagerClosureV1Controller.kt` · `VenueClosureCreateRequest.kt` · `VenueManagerClosureViews.kt`,
 * 커밋 `77071950`)를 직접 읽어 미러링했다(설계 §2-0). `api-spec.md` 가 게시되면 대조해 드리프트를 확인할 것.
 *
 * `courtTypes.ts` 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한 필드를 파생·개명하지 않는다.
 * 유형·적용범위·요일 라벨은 서버가 내려주는 필드(`typeLabel` 등)를 그대로 쓰고 클라이언트에
 * 하드코딩하지 않는다.
 */

export type VenueClosureType = 'HOLIDAY' | 'BLOCK' | 'RECURRING_BLOCK' | 'LESSON'

export type VenueClosureApplyScope = 'ALL_COURTS' | 'SELECTED_COURTS'

export interface VenueClosureCourtView {
  courtId: number
  courtName: string
}

export interface VenueClosureItemView {
  closureId: number
  type: VenueClosureType
  typeLabel: string
  /** "일시" 한 칸 — 날짜/요일/시간/종일 표기를 서버가 조립해 준다. */
  periodLabel: string
  allDay: boolean
  startDate: string | null
  endDate: string | null
  recurringDayOfWeeks: string[]
  recurringDayOfWeekLabels: string[]
  /** "HH:mm". 자정 마감은 "24:00". `allDay=true` 면 null. */
  startTime: string | null
  endTime: string | null
  applyScope: VenueClosureApplyScope
  applyScopeLabel: string
  /** `applyScope='ALL_COURTS'` 면 빈 배열. */
  courts: VenueClosureCourtView[]
  /** 레슨명. `LESSON` 만 값이 있다. 🚨 사용자 예약 화면에 표시된다. */
  title: string | null
  /** 차단 사유. 🚨 내부용 — 사용자 표면에는 노출하지 않는다(이 관리자 화면에는 그대로 표시한다). */
  reason: string | null
  registeredBy: string | null
  registeredByType: string
  registeredByTypeLabel: string
  registeredAt: string
}

export interface VenueClosureSummaryView {
  /** "yyyy-MM" */
  month: string
  /** 이번 달 휴무로 막히는 날짜 수(날짜 기준 중복 제거). */
  holidayDayCount: number
  /** 이번 달 차단 블록 "발생" 수(등록 건수가 아니다). */
  blockCount: number
  /** 탭 뱃지용 유형별 등록 건수. 4키가 항상 존재한다. */
  typeCounts: Record<VenueClosureType, number>
}

export interface VenueClosureListView {
  baseTime: string
  venueId: string
  /** 지금 등록·삭제할 수 있는가 = 쓰기 2EP 가 통과하는 조건과 같은 판정. */
  editable: boolean
  editBlockReason: string | null
  editBlockNotice: string | null
  summary: VenueClosureSummaryView
  items: VenueClosureItemView[]
}

/** `POST` 응답 — ①과 같은 목록에 이번 등록의 충돌 정보를 덧붙인 것. */
export interface VenueClosureCreatedView {
  closureId: number
  forced: boolean
  conflictReservationCount: number
  conflictNotice: string | null
  list: VenueClosureListView
}

/**
 * `POST` 요청 바디. 모든 필드가 유형에 따라 조건부이고, 유형별 필수조합은 서버 도메인이 유일한
 * 판정처다(`closureViewModel.ts` 의 클라이언트 검증은 왕복을 줄이기 위한 보조일 뿐).
 */
export interface VenueClosureCreateInput {
  type: VenueClosureType
  /** "yyyy-MM-dd". HOLIDAY·BLOCK 필수. */
  startDate: string | null
  endDate: string | null
  /** RECURRING_BLOCK·LESSON 필수(1개 이상). */
  recurringDayOfWeeks: string[]
  /** "HH:mm". BLOCK·RECURRING_BLOCK·LESSON 필수, HOLIDAY 는 금지(null). */
  startTime: string | null
  endTime: string | null
  applyScope: VenueClosureApplyScope
  /** `applyScope='SELECTED_COURTS'` 면 1개 이상, `'ALL_COURTS'` 면 빈 배열. */
  courtIds: number[]
  /** 레슨명. `LESSON` 필수, 그 외 금지. */
  title: string | null
  /** 차단 사유. `BLOCK`·`RECURRING_BLOCK` 필수, 그 외 선택. */
  reason: string | null
  /** 확정/진행중 예약과 겹쳐도 강행할 것인가. 기본 false. 저장되지 않는 요청 1회성 플래그. */
  force: boolean
}

/** 유형 탭 카탈로그(forward-only) — 뱃지 순서·표시 라벨은 화면 고정값이다(#604 요구사항 목록 순서). */
export const CLOSURE_TYPE_CATALOG: { value: VenueClosureType; label: string }[] = [
  { value: 'HOLIDAY', label: '휴무일' },
  { value: 'BLOCK', label: '차단 시간' },
  { value: 'RECURRING_BLOCK', label: '반복 차단' },
  { value: 'LESSON', label: '레슨' },
]
